import { forwardRef } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';

import classNames from '@/utils/classnames';

export const Select = SelectPrimitive.Root;
export const SelectValue = SelectPrimitive.Value;
export const SelectGroup = SelectPrimitive.Group;

interface SelectTriggerProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  error?: boolean;
}

export const SelectTrigger = forwardRef<React.ElementRef<typeof SelectPrimitive.Trigger>, SelectTriggerProps>(
  ({ className, children, error, ...props }, ref) => (
    <SelectPrimitive.Trigger
      ref={ref}
      className={classNames(
        'flex h-9 w-full items-center justify-between rounded-lg border bg-surface-2 px-3 text-sm text-fg outline-none transition-colors',
        'data-placeholder:text-fg-disabled',
        'disabled:cursor-not-allowed disabled:opacity-50',
        error
          ? 'border-danger focus:border-danger focus:ring-1 focus:ring-danger/20'
          : 'border-line focus:border-brand-border focus:ring-1 focus:ring-brand/15',
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <CaretDownIcon size={14} weight="bold" className="ml-2 shrink-0 text-fg-tertiary" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  ),
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

export const SelectContent = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      sideOffset={4}
      className={classNames(
        'z-dropdown max-h-(--radix-select-content-available-height) min-w-(--radix-select-trigger-width) overflow-hidden rounded-lg border border-line-strong bg-surface-1 shadow-(--shadow-overlay)',
        'data-[state=open]:animate-fade-in',
        className,
      )}
      {...props}
    >
      <SelectPrimitive.Viewport className="p-1">{children}</SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

interface SelectItemProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> {
  /** Sub-line under the option label explaining what picking it does. Not shown in the trigger. */
  description?: string;
}

export const SelectItem = forwardRef<React.ElementRef<typeof SelectPrimitive.Item>, SelectItemProps>(
  ({ className, children, description, ...props }, ref) => (
    <SelectPrimitive.Item
      ref={ref}
      className={classNames(
        'relative flex w-full cursor-pointer select-none items-center rounded-md py-2 pl-3 pr-8 text-sm text-fg outline-none',
        'data-highlighted:bg-surface-3 data-highlighted:text-fg',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <span className="flex min-w-0 flex-col gap-0.5">
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        {description && <span className="text-2xs leading-3.5 text-fg-tertiary">{description}</span>}
      </span>
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon size={14} weight="bold" className="text-brand-text" />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  ),
);
SelectItem.displayName = SelectPrimitive.Item.displayName;
